import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Paper,
  Alert,
  CircularProgress,
  Avatar,
  Collapse,
} from '@mui/material';
import {
  AutoAwesome as WandIcon,
  PhotoCamera as PhotoIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { specialRequestsApi, teamApi, salonSettingsApi } from '../../firebase/services';
import { TeamMember, SalonSettings, DEFAULT_SALON_SETTINGS } from '../../types';
import heic2any from 'heic2any';

const isHeic = (file: File) => {
  const n = file.name.toLowerCase();
  return file.type === 'image/heic' || file.type === 'image/heif' || n.endsWith('.heic') || n.endsWith('.heif');
};

const timeToMinutes = (t: string) => {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const validateSchedule = (
  date: string,
  time: string,
  settings: SalonSettings,
  t: (key: string, opts?: object) => string
): string | null => {
  if (!date && !time) return null;

  if (date) {
    const dayOfWeek = new Date(date + 'T12:00:00').getDay();
    const schedule = settings.businessHours[dayOfWeek];

    if (!schedule || !schedule.isOpen) {
      return t('specialRequest.closedDay', { day: t(`specialRequest.days.${dayOfWeek}`) });
    }

    if (settings.holidays?.includes(date)) {
      return t('specialRequest.closedHoliday');
    }

    if (time) {
      const selectedMin = timeToMinutes(time);
      const openMin = timeToMinutes(schedule.openTime);
      const closeMin = timeToMinutes(schedule.closeTime);

      if (selectedMin < openMin) {
        return t('specialRequest.opensAt', { time: schedule.openTime });
      }
      if (selectedMin >= closeMin) {
        return t('specialRequest.closesAt', { time: schedule.closeTime });
      }
    }
  }

  return null;
};

const SpecialRequestForm: React.FC = () => {
  const { t } = useTranslation();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [salonSettings, setSalonSettings] = useState<SalonSettings>(DEFAULT_SALON_SETTINGS);
  const [form, setForm] = useState({
    clientName: '',
    clientPhone: '',
    clientEmail: '',
    description: '',
    preferredDate: '',
    preferredTime: '',
    preferredStylistId: '',
  });
  const [scheduleError, setScheduleError] = useState('');
  const [photo, setPhoto] = useState<File | null>(null);
  const [photoPreview, setPhotoPreview] = useState<string>('');
  const [converting, setConverting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    teamApi.getAll().then(members => setTeamMembers(members.filter(m => m.isActive)));
    salonSettingsApi.get().then(setSalonSettings);
  }, []);

  const handleDateChange = (date: string) => {
    setForm(p => ({ ...p, preferredDate: date }));
    const msg = validateSchedule(date, form.preferredTime, salonSettings, t);
    setScheduleError(msg || '');
  };

  const handleTimeChange = (time: string) => {
    setForm(p => ({ ...p, preferredTime: time }));
    const msg = validateSchedule(form.preferredDate, time, salonSettings, t);
    setScheduleError(msg || '');
  };

  const getTimeRange = () => {
    if (!form.preferredDate) return { min: '00:00', max: '23:59' };
    const dayOfWeek = new Date(form.preferredDate + 'T12:00:00').getDay();
    const schedule = salonSettings.businessHours[dayOfWeek];
    if (!schedule || !schedule.isOpen) return { min: '00:00', max: '23:59' };
    return { min: schedule.openTime, max: schedule.closeTime };
  };

  const getHoursHint = () => {
    if (!form.preferredDate) return null;
    const dayOfWeek = new Date(form.preferredDate + 'T12:00:00').getDay();
    const schedule = salonSettings.businessHours[dayOfWeek];
    if (!schedule || !schedule.isOpen) return null;
    return t('specialRequest.openHours', { open: schedule.openTime, close: schedule.closeTime });
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');

    let finalFile = file;

    if (isHeic(file)) {
      setConverting(true);
      try {
        const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        const blob = Array.isArray(converted) ? converted[0] : converted;
        finalFile = new File([blob], file.name.replace(/\.heic$/i, '.jpg').replace(/\.heif$/i, '.jpg'), { type: 'image/jpeg' });
      } catch {
        setError(t('specialRequest.errorHeic'));
        setConverting(false);
        return;
      }
      setConverting(false);
    }

    setPhoto(finalFile);
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target?.result as string);
    reader.readAsDataURL(finalFile);
  };

  const handleSubmit = async () => {
    setError('');

    if (!form.clientName || !form.clientPhone || !form.description) {
      setError(t('specialRequest.errorRequired'));
      return;
    }

    const scheduleMsg = validateSchedule(form.preferredDate, form.preferredTime, salonSettings, t);
    if (scheduleMsg) {
      setScheduleError(scheduleMsg);
      setError(t('specialRequest.errorSchedule'));
      return;
    }

    setSubmitting(true);
    try {
      await specialRequestsApi.create({
        clientName: form.clientName,
        clientPhone: form.clientPhone,
        clientEmail: form.clientEmail || undefined,
        description: form.description,
        preferredDate: form.preferredDate || undefined,
        preferredTime: form.preferredTime || undefined,
        preferredStylistId: form.preferredStylistId || undefined,
      }, photo || undefined);

      setSuccess(true);
      setForm({ clientName: '', clientPhone: '', clientEmail: '', description: '', preferredDate: '', preferredTime: '', preferredStylistId: '' });
      setPhoto(null);
      setPhotoPreview('');
      setScheduleError('');
    } catch (err) {
      console.error('SpecialRequest submit error:', err);
      setError(t('specialRequest.errorGeneral'));
    } finally {
      setSubmitting(false);
    }
  };

  const today = format(new Date(), 'yyyy-MM-dd');
  const timeRange = getTimeRange();

  return (
    <Box sx={{ bgcolor: '#F8F4EF', py: 8 }}>
      <Container maxWidth="md">
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 56,
              height: 56,
              borderRadius: '50%',
              bgcolor: 'primary.main',
              mb: 2,
            }}
          >
            <WandIcon sx={{ color: '#fff', fontSize: 28 }} />
          </Box>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('specialRequest.sectionTitle')}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 560, mx: 'auto' }}>
            {t('specialRequest.sectionSubtitle')}
          </Typography>
        </Box>

        <Paper elevation={0} sx={{ borderRadius: 4, p: { xs: 3, md: 5 }, border: '1px solid', borderColor: 'divider' }}>
          <Collapse in={success}>
            <Alert
              severity="success"
              icon={<CheckIcon />}
              action={
                <Button size="small" onClick={() => setSuccess(false)}>
                  <CloseIcon fontSize="small" />
                </Button>
              }
              sx={{ mb: 3, borderRadius: 2 }}
            >
              <Typography fontWeight={600}>{t('specialRequest.successTitle')}</Typography>
              {t('specialRequest.successMessage')}
            </Alert>
          </Collapse>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2.5 }}>
            <TextField
              label={t('specialRequest.clientName')}
              value={form.clientName}
              onChange={e => setForm(p => ({ ...p, clientName: e.target.value }))}
              fullWidth
            />

            <TextField
              label={t('specialRequest.phone')}
              value={form.clientPhone}
              onChange={e => setForm(p => ({ ...p, clientPhone: e.target.value }))}
              fullWidth
            />

            <TextField
              label={t('specialRequest.email')}
              type="email"
              value={form.clientEmail}
              onChange={e => setForm(p => ({ ...p, clientEmail: e.target.value }))}
              fullWidth
            />

            <FormControl fullWidth>
              <InputLabel>{t('specialRequest.stylist')}</InputLabel>
              <Select
                value={form.preferredStylistId}
                label={t('specialRequest.stylist')}
                onChange={e => setForm(p => ({ ...p, preferredStylistId: e.target.value }))}
              >
                <MenuItem value="">{t('specialRequest.stylistAny')}</MenuItem>
                {teamMembers.map(m => (
                  <MenuItem key={m.id} value={m.id}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar src={m.imageUrl || ''} sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main' }}>
                        {!m.imageUrl && m.name.charAt(0)}
                      </Avatar>
                      {m.name}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label={t('specialRequest.date')}
              type="date"
              value={form.preferredDate}
              onChange={e => handleDateChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: today }}
              fullWidth
              error={!!scheduleError && !!form.preferredDate}
            />

            <TextField
              label={t('specialRequest.time')}
              type="time"
              value={form.preferredTime}
              onChange={e => handleTimeChange(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: timeRange.min, max: timeRange.max }}
              fullWidth
              error={!!scheduleError && !!form.preferredTime}
              helperText={
                scheduleError
                  ? <span style={{ color: '#d32f2f' }}>{scheduleError}</span>
                  : getHoursHint()
                    ? <span style={{ color: '#666' }}>{getHoursHint()}</span>
                    : undefined
              }
            />

            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <TextField
                label={t('specialRequest.description')}
                value={form.description}
                onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                fullWidth
                multiline
                rows={4}
                placeholder={t('specialRequest.descriptionPlaceholder')}
              />
            </Box>

            <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1' } }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('specialRequest.photo')}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                {photoPreview ? (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      component="img"
                      src={photoPreview}
                      alt="Référence"
                      sx={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 2, border: '2px solid', borderColor: 'divider' }}
                    />
                    <Button
                      size="small"
                      sx={{ position: 'absolute', top: 4, right: 4, minWidth: 0, p: 0.3, bgcolor: 'error.main', color: '#fff', borderRadius: '50%', '&:hover': { bgcolor: 'error.dark' } }}
                      onClick={() => { setPhoto(null); setPhotoPreview(''); }}
                    >
                      <CloseIcon sx={{ fontSize: 14 }} />
                    </Button>
                  </Box>
                ) : (
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={converting ? <CircularProgress size={16} /> : <PhotoIcon />}
                    disabled={converting}
                    sx={{ borderRadius: 2, textTransform: 'none' }}
                  >
                    {converting ? t('specialRequest.photoConverting') : t('specialRequest.photoButton')}
                    <input
                      type="file"
                      hidden
                      accept="image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif"
                      onChange={handlePhotoChange}
                    />
                  </Button>
                )}
                <Typography variant="caption" color="text.secondary">
                  {t('specialRequest.photoFormats')}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={submitting || !!scheduleError}
              startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <WandIcon />}
              sx={{ borderRadius: 3, px: 4, textTransform: 'none', fontWeight: 600 }}
            >
              {submitting ? t('specialRequest.submitting') : t('specialRequest.submit')}
            </Button>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default SpecialRequestForm;
